import { configService } from '$lib/services';

import { streamingLoggerStore } from '@components/room/streaming-debugger/stores/logger';
import { LogType } from '@shared/types';
import { StreamingType, StreamProvider } from '@shared/types/room';

import Events from './eventsDispatcher/events';
import Emitter from './eventsDispatcher';
import MediaStreamController from './mediaStreamController';
import { StreamingStatistics } from './statistics';
import StreamingController from './streamingController';
import type {
    BroadcastConfig,
    PlayerConfig,
} from './types';

class StreamingService {
    streamingControllerBroadcast: StreamingController;
    streamingControllerDelivery: StreamingController;
    mediaStreamController: MediaStreamController;
    emitter: typeof Emitter;
    events: typeof Events;
    statsController: StreamingStatistics;
    broadcastIsRunning = false;
    deliveryIsRunning = false;
    broadcastPromise: Promise<unknown> = Promise.resolve();
    deliveryPromise: Promise<unknown> = Promise.resolve();

    constructor() {
        this.emitter = Emitter;
        this.events = Events;
        this.mediaStreamController = new MediaStreamController();
        this.streamingControllerBroadcast = new StreamingController();
        this.streamingControllerDelivery = new StreamingController();
        this.statsController = new StreamingStatistics();
    }

    public getCurrentBroadcastProvider() {
        return this.streamingControllerBroadcast.provider?.getType();
    }

    getStreamingProviderBroadcast(providerType: StreamProvider) {
        const currentProvider = this.streamingControllerBroadcast.provider;

        if (!currentProvider || currentProvider.getType() !== providerType) {
            this.streamingControllerBroadcast.setProvider(providerType);
            this.statsController.setStreamProvider(providerType);
        }

        return this.streamingControllerBroadcast.provider;
    }

    getStreamingProviderDelivery(providerType: StreamProvider) {
        const currentProvider = this.streamingControllerDelivery.provider;

        if (!currentProvider || currentProvider.getType() !== providerType) {
            this.streamingControllerDelivery.setProvider(providerType);
            this.statsController.setStreamProvider(providerType);
        }

        return this.streamingControllerDelivery.provider;
    }

    public async startBroadcast(config:BroadcastConfig) {
        const provider = configService.getStreamProvider();

        if (this.broadcastIsRunning) {
            streamingLoggerStore.log(
                { type: LogType.AppBroadcast, message: 'StreamingService stopBroadcast on start' },
                { provider: this.streamingControllerBroadcast.provider?.getType() },
                { includeExtendedData: true }
            );
            this.broadcastPromise =
                this.broadcastPromise
                    .then(() => this.streamingControllerBroadcast.provider?.stopBroadcast());
        }

        streamingLoggerStore.log(
            { type: LogType.AppBroadcast, message: 'StreamingService startBroadcast' },
            { provider: provider },
            { includeExtendedData: true }
        );
        this.broadcastIsRunning = true;
        this.broadcastPromise =
            this.broadcastPromise
                .then(() => this.getStreamingProviderBroadcast(provider)?.startBroadcast(config));
        return this.broadcastPromise;
    }

    public async startDelivery(config: PlayerConfig) {
        if (!config.provider) {
            return;
        }

        if (this.deliveryIsRunning) {
            streamingLoggerStore.log(
                { type: LogType.AppDelivery, message: 'StreamingService stopDelivery on start' },
                { provider: this.streamingControllerDelivery.provider?.getType() },
                { includeExtendedData: true }
            );
            this.deliveryPromise =
                this.deliveryPromise
                    .then(() => this.streamingControllerDelivery.provider?.stopDelivery());
        }

        streamingLoggerStore.log(
            {
                type: LogType.AppDelivery,
                message: 'StreamingService startDelivery',
            },
            { provider: config.provider },
            { includeExtendedData: true }
        );
        this.deliveryIsRunning = true;
        this.deliveryPromise =
            this.deliveryPromise
                .then(() => this.getStreamingProviderDelivery(config.provider)?.startDelivery(config));

        return this.deliveryPromise;
    }

    public destroyStreamingProvider() {
        this.streamingControllerBroadcast.destroyProvider();
        this.streamingControllerDelivery.destroyProvider();
    }

    public async stopBroadcast() {
        if (!this.broadcastIsRunning) {
            return this.broadcastPromise;
        }
        streamingLoggerStore.log(
            { type: LogType.AppBroadcast, message: 'StreamingService stopBroadcast' },
            { provider: this.streamingControllerBroadcast.provider?.getType() },
            { includeExtendedData: true }
        );

        this.broadcastPromise =
            this.broadcastPromise
                .then(() => this.streamingControllerBroadcast.provider?.stopBroadcast());
        this.broadcastIsRunning = false;
        return this.broadcastPromise;
    }

    public async stopDelivery(videoElement: HTMLVideoElement) {
        streamingLoggerStore.log(
            { type: LogType.AppDelivery, message: 'StreamingService stopDelivery' },
            { provider: this.streamingControllerDelivery.provider?.getType() },
            { includeExtendedData: true }
        );
        if (!this.deliveryIsRunning) {
            streamingLoggerStore.log(
                { type: LogType.AppDelivery, message: 'StreamingService stopDelivery: delivery is not running' },
                { provider: this.streamingControllerDelivery.provider?.getType() },
                { includeExtendedData: true }
            );
            return this.deliveryPromise;
        }
        const isCloudflare =
            this.streamingControllerDelivery.provider?.getType() === StreamProvider.CloudFlare;

        if (videoElement && isCloudflare) {
            this.emitter.emit(
                this.events.STREAMING_STATS_DETACH_VIDEO_ON_ERROR,
                videoElement,
                StreamingType.Delivery
            );
        }

        this.deliveryPromise =
            this.deliveryPromise.then(() => this.streamingControllerDelivery.provider?.stopDelivery());
        this.deliveryIsRunning = false;
        return this.deliveryPromise;
    }

    public async refreshBroadcast(byPlayerHealth = false) {
        streamingLoggerStore.log(
            { type: LogType.AppBroadcast, message: 'StreamingService restartBroadcast' },
            { provider: this.streamingControllerBroadcast.provider?.getType() },
            { includeExtendedData: true }
        );
        return this.broadcastPromise =
            this.broadcastPromise
                .then(() => this.streamingControllerBroadcast.provider?.restartBroadcast(byPlayerHealth));
    }
}

const streamingService = new StreamingService();

export default streamingService;
